/**
 * PATH: packages/tenant-shared/src/lib/oauthNavigation.ts
 * FILE_ID: TENANT-SHARED-LIB-OAUTH-NAVIGATION-001
 * PURPOSE: Display-mode-aware navigation to an external OAuth provider
 *          (Microsoft / Google consent screens).
 * WHY: Operator 2026-07-08: connecting a mailbox from the INSTALLED app
 *      "redirected me back to chrome instead of the app". The flow did
 *      `window.location.href = authorization_url`, which navigates the
 *      standalone PWA window itself out of app scope — the OS hands the
 *      whole session to the browser, and the OAuth callback then lands in
 *      a Chrome tab, never returning to the installed app. Opening the
 *      provider in a separate browsing context keeps the PWA window alive
 *      underneath; when the user returns, the accounts refresh-on-focus
 *      picks up the new mailbox.
 * NOTE: packages/ui cannot depend on tenant-shared (boundary) — its
 *       EmailAccountsTab carries an inline copy of this check. Keep them
 *       in lockstep.
 */

/** True when running as an installed app (standalone PWA window). */
export function isStandaloneDisplayMode(): boolean {
  try {
    if (typeof window === 'undefined') return false
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true
    // iOS Safari home-screen apps expose navigator.standalone instead.
    return (navigator as Navigator & { standalone?: boolean }).standalone === true
  } catch {
    return false
  }
}

/**
 * Navigate to an OAuth provider's authorization URL.
 *
 * - Browser tab: plain same-window navigation (unchanged behaviour — the
 *   provider redirects straight back into this tab).
 * - Installed PWA: open in a NEW browsing context (custom tab / browser
 *   tab) so the app window is never navigated out of scope. If the popup
 *   is blocked, fall back to same-window navigation rather than doing
 *   nothing.
 */
export function navigateToOAuthProvider(url: string): void {
  if (isStandaloneDisplayMode()) {
    const opened = window.open(url, '_blank', 'noopener,noreferrer')
    if (opened) return
  }
  window.location.href = url
}
